/**
 * A ordem em que CADA OPERADOR vê os filtros (e os Kanbans) no painel.
 *
 * Ponto 22 da Gisele (20/10/2026): "Ordem dos Kanbans: cada operador pode
 * reordenar os Kanbans, e se possível a preferência fica salva."
 *
 * ## Por grupo, e não uma ordem só
 *
 * O painel mostra dois blocos -- os compartilhados em cima, as visualizações
 * pessoais embaixo. Uma ordem única não teria como ser obedecida nesse
 * desenho: arrastar um pessoal para cima de um compartilhado pediria uma
 * posição que o layout não oferece. Então cada bloco tem a sua ordem, e
 * arrastar entre blocos não existe.
 *
 * ## A ordem salva não manda na LISTA, só na posição
 *
 * Quem decide quais filtros existem continua sendo a api. Esta ordem só diz
 * onde cada um fica. Por isso:
 *
 *   - id na ordem salva que não está mais na lista é IGNORADO (o filtro foi
 *     apagado, ou deixou de ser compartilhado com este operador);
 *   - filtro da lista que não está na ordem salva vai para o FIM do grupo, na
 *     ordem que a api mandou.
 *
 * A segunda regra é a que importa: um Kanban novo da equipe tem de aparecer.
 * Se o desconhecido fosse descartado, o operador que reordenou uma vez
 * pararia de receber qualquer filtro criado depois -- e sem erro nenhum, só
 * um filtro que "não chegou para ele".
 *
 * ## Por operador, no navegador
 *
 * É preferência de quem está olhando, e não dado do filtro -- dois operadores
 * no mesmo computador não herdam a ordem um do outro, por isso o id entra na
 * chave. Não vai para a api de propósito: nada aqui precisa ser conhecido por
 * outra máquina, e a alternativa custaria uma coluna e um endpoint.
 *
 * A contrapartida é honesta: trocar de navegador devolve a ordem da api.
 */

const PREFIXO = 'alodesk:filtros:ordem';

/** Os grupos do painel. Nomes curtos porque entram na chave do localStorage. */
export const GRUPO_COMPARTILHADO = 'compartilhados';
export const GRUPO_PESSOAL = 'pessoais';

/**
 * A chave de um grupo para um operador.
 *
 * Sem o id, a ordem seria do COMPUTADOR: no balcão da SysFar, quem entrasse
 * depois herdaria a ordem de quem entrou antes.
 */
export const chaveDaOrdem = (operadorId, grupo) => `${PREFIXO}:${operadorId ?? 'anonimo'}:${grupo}`;

/**
 * Lê a ordem salva.
 *
 * Qualquer problema devolve lista vazia, que significa "use a ordem da api" --
 * o painel nunca fica sem filtro por causa disto. Vale para o localStorage
 * indisponível (aba privada, dado bloqueado), para o JSON corrompido e para o
 * conteúdo que não é uma lista.
 */
export const lerOrdem = (operadorId, grupo) => {
    try {
        const bruto = localStorage.getItem(chaveDaOrdem(operadorId, grupo));
        const lido = bruto ? JSON.parse(bruto) : null;

        return Array.isArray(lido) ? lido.map(Number).filter((n) => !Number.isNaN(n)) : [];
    } catch {
        return [];
    }
};

/**
 * Grava a ordem de um grupo.
 *
 * @returns {boolean} se conseguiu gravar -- o chamador pode avisar quem
 *   arrastou que a posição não vai sobreviver ao recarregar, em vez de
 *   prometer silenciosamente algo que não aconteceu.
 */
export const gravarOrdem = (operadorId, grupo, ids) => {
    try {
        localStorage.setItem(chaveDaOrdem(operadorId, grupo), JSON.stringify(ids.map(Number)));
        return true;
    } catch {
        return false;
    }
};

/**
 * Aplica a ordem salva a uma lista de filtros.
 *
 * @param {Array<object>} filtros os filtros do grupo, na ordem da api
 * @param {Array<number>} ordem os ids na ordem que o operador escolheu
 * @returns {Array<object>} os MESMOS filtros, reordenados
 */
export const aplicarOrdem = (filtros, ordem) => {
    const lista = Array.isArray(filtros) ? filtros : [];
    if (!Array.isArray(ordem) || ordem.length === 0) return lista;

    const posicao = new Map(ordem.map((id, i) => [Number(id), i]));

    // `sort` sobre uma COPIA: ordenar no lugar mexeria no array que veio do
    // redux, e o React não tem como perceber a mudança.
    return lista.slice().sort((a, b) => {
        const pa = posicao.has(Number(a.id)) ? posicao.get(Number(a.id)) : Infinity;
        const pb = posicao.has(Number(b.id)) ? posicao.get(Number(b.id)) : Infinity;

        // Os dois desconhecidos empatam, e `Array.prototype.sort` é estável
        // desde o ES2019: eles ficam no fim na ordem que a api mandou.
        return pa - pb;
    });
};

/** A ordem salva de um grupo, já aplicada -- o que o painel precisa. */
export const filtrosNaOrdemDoOperador = (filtros, operadorId, grupo) => (
    aplicarOrdem(filtros, lerOrdem(operadorId, grupo))
);
